import { Component, computed, Input, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FeaturesSettings,
  FeaturesContent,
  FeaturesLayoutVariant,
  FeatureItem,
  SECTION_THEME,
} from './section-renderer.models';

/** Feature settings stored in settings (shared across languages) */
interface FeatureSettingsItem {
  id: string;
  icon: string;
  imageId?: string | null;
  imageUrl?: string; // Resolved URL from mediaUrlMap
}

/** Feature text stored in content per language */
interface FeatureTextItem {
  title: string;
  description: string;
}

/** Resolved feature with merged settings and content */
interface ResolvedFeature {
  id: string;
  title: string;
  description: string;
  icon: string;
  imageUrl?: string;
}

/** Icon name to SVG path mapping */
const ICON_PATHS: Record<string, string> = {
  star: 'M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z',
  heart: 'M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z',
  check: 'M4.5 12.75l6 6 9-13.5',
  calendar: 'M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5',
  clock: 'M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z',
  users: 'M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z',
  chart: 'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z',
  shield: 'M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z',
  rocket: 'M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z',
  lightning: 'M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z',
  globe: 'M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418',
};

/**
 * Full-width Features section renderer.
 * Layout variants:
 * - grid: Features in a responsive grid
 * - alternating: Features alternating left/right with images
 * - icon-list: Simple icon + text list (default)
 *
 * Features structure follows the Team/Stats pattern:
 * - Settings: contains feature definitions (id, icon) - shared across languages
 * - Content: contains featureTexts keyed by feature ID (title, description) - localized
 */
@Component({
  selector: 'dcp-features-renderer',
  standalone: true,
  imports: [CommonModule],
  template: `
    @switch (layoutVariant()) {
      <!-- Grid Layout -->
      @case ('grid') {
        <section
          class="relative py-24 sm:py-32"
          [style.background-color]="settings().backgroundColor || theme.backgroundColor"
        >
          <div class="mx-auto max-w-7xl px-6 lg:px-8">
            @if (content().title || content().subtitle) {
              <div class="mx-auto max-w-2xl text-center mb-16">
                @if (content().title) {
                  <h2 class="text-base font-semibold leading-7" [style.color]="theme.primaryColor">
                    Features
                  </h2>
                  <p class="mt-2 text-3xl font-bold tracking-tight sm:text-4xl" [style.color]="theme.textColor">
                    {{ content().title }}
                  </p>
                }
                @if (content().subtitle) {
                  <p class="mt-6 text-lg leading-8" [style.color]="theme.textMutedColor">
                    {{ content().subtitle }}
                  </p>
                }
              </div>
            }
            <div class="mx-auto max-w-2xl lg:max-w-none">
              <dl class="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
                @for (feature of resolvedFeatures(); track feature.id) {
                  <div class="flex flex-col">
                    <dt class="flex items-center gap-x-3 text-base font-semibold leading-7" [style.color]="theme.textColor">
                      <div
                        class="flex h-10 w-10 items-center justify-center rounded-lg"
                        [style.background-color]="theme.primaryColor"
                      >
                        <svg class="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" [attr.d]="getIconPath(feature.icon)" />
                        </svg>
                      </div>
                      {{ feature.title }}
                    </dt>
                    <dd class="mt-4 flex flex-auto flex-col text-base leading-7" [style.color]="theme.textMutedColor">
                      <p class="flex-auto">{{ feature.description }}</p>
                    </dd>
                  </div>
                }
              </dl>
            </div>
          </div>
        </section>
      }

      <!-- Alternating Layout -->
      @case ('alternating') {
        <section
          class="relative overflow-hidden py-24 sm:py-32"
          [style.background-color]="settings().backgroundColor || theme.backgroundColor"
        >
          <div class="mx-auto max-w-7xl px-6 lg:px-8">
            @if (content().title || content().subtitle) {
              <div class="mx-auto max-w-2xl text-center mb-16">
                @if (content().title) {
                  <h2 class="text-3xl font-bold tracking-tight sm:text-4xl" [style.color]="theme.textColor">
                    {{ content().title }}
                  </h2>
                }
                @if (content().subtitle) {
                  <p class="mt-6 text-lg leading-8" [style.color]="theme.textMutedColor">
                    {{ content().subtitle }}
                  </p>
                }
              </div>
            }
            <div class="space-y-24">
              @for (feature of resolvedFeatures(); track feature.id; let i = $index) {
                <div class="flex flex-col gap-16 lg:flex-row lg:items-center" [class.lg:flex-row-reverse]="i % 2 === 1">
                  <!-- Image -->
                  <div class="flex-1">
                    @if (feature.imageUrl) {
                      <img
                        [src]="feature.imageUrl"
                        [alt]="feature.title"
                        class="w-full rounded-xl shadow-xl ring-1 ring-gray-400/10"
                      />
                    } @else {
                      <div
                        class="aspect-[16/9] w-full rounded-xl flex items-center justify-center"
                        [style.background-color]="theme.surfaceColor"
                      >
                        <svg class="h-16 w-16 opacity-30" fill="none" viewBox="0 0 24 24" stroke-width="1" [style.stroke]="theme.textMutedColor">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                        </svg>
                      </div>
                    }
                  </div>
                  <!-- Content -->
                  <div class="flex-1">
                    <div class="lg:max-w-lg">
                      <h3 class="text-2xl font-bold tracking-tight sm:text-3xl" [style.color]="theme.textColor">
                        {{ feature.title }}
                      </h3>
                      <p class="mt-6 text-lg leading-8" [style.color]="theme.textMutedColor">
                        {{ feature.description }}
                      </p>
                    </div>
                  </div>
                </div>
              }
            </div>
          </div>
        </section>
      }

      <!-- Icon List Layout (default) -->
      @default {
        <section
          class="relative py-24 sm:py-32"
          [style.background-color]="settings().backgroundColor || theme.surfaceColor"
        >
          <div class="mx-auto max-w-7xl px-6 lg:px-8">
            <div class="mx-auto grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 sm:gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-2">
              <!-- Left column: Title & features -->
              <div class="lg:pr-8 lg:pt-4">
                <div class="lg:max-w-lg">
                  @if (content().title) {
                    <h2 class="text-base font-semibold leading-7" [style.color]="theme.primaryColor">
                      Features
                    </h2>
                    <p class="mt-2 text-3xl font-bold tracking-tight sm:text-4xl" [style.color]="theme.textColor">
                      {{ content().title }}
                    </p>
                  }
                  @if (content().subtitle) {
                    <p class="mt-6 text-lg leading-8" [style.color]="theme.textMutedColor">
                      {{ content().subtitle }}
                    </p>
                  }
                  <dl class="mt-10 max-w-xl space-y-8 text-base leading-7 lg:max-w-none">
                    @for (feature of resolvedFeatures(); track feature.id) {
                      <div class="relative pl-9">
                        <dt class="inline font-semibold" [style.color]="theme.textColor">
                          <div
                            class="absolute left-1 top-1 h-5 w-5 rounded-full flex items-center justify-center"
                            [style.background-color]="theme.primaryColor"
                          >
                            <svg class="h-3 w-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <path stroke-linecap="round" stroke-linejoin="round" [attr.d]="getIconPath(feature.icon)" />
                            </svg>
                          </div>
                          {{ feature.title }}
                        </dt>
                        <dd class="inline" [style.color]="theme.textMutedColor">
                          &mdash; {{ feature.description }}
                        </dd>
                      </div>
                    }
                  </dl>
                </div>
              </div>
              <!-- Right column: Decorative image -->
              <div class="flex items-start justify-center lg:justify-start lg:order-first">
                @if (sectionImageUrl()) {
                  <img
                    [src]="sectionImageUrl()"
                    alt="Feature illustration"
                    class="w-full max-w-full rounded-xl shadow-xl ring-1 ring-gray-400/10 object-cover"
                  />
                } @else {
                  <div
                    class="w-full aspect-square rounded-xl flex items-center justify-center"
                    [style.background-color]="theme.backgroundColor"
                  >
                    <svg class="h-24 w-24 opacity-20" fill="none" viewBox="0 0 24 24" stroke-width="1" [style.stroke]="theme.textMutedColor">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                    </svg>
                  </div>
                }
              </div>
            </div>
          </div>
        </section>
      }
    }
  `,
})
export class FeaturesRendererComponent {
  /** Theme injected from parent fullwidth-preview */
  private readonly themeSignal = inject(SECTION_THEME);
  protected get theme() { return this.themeSignal(); }

  private readonly _settings = signal<FeaturesSettings & { features?: FeatureSettingsItem[]; sectionImageId?: string; sectionImageUrl?: string }>({});
  private readonly _content = signal<FeaturesContent & { featureTexts?: Record<string, FeatureTextItem> }>({ features: [] });

  @Input() set settingsInput(value: Record<string, unknown>) {
    this._settings.set(value as FeaturesSettings & { features?: FeatureSettingsItem[]; sectionImageId?: string; sectionImageUrl?: string });
  }

  @Input() set contentInput(value: Record<string, unknown>) {
    const content = value as unknown as FeaturesContent & { featureTexts?: Record<string, FeatureTextItem> };
    this._content.set({ ...content, features: content.features || [] });
  }

  readonly settings = this._settings.asReadonly();
  readonly content = this._content.asReadonly();

  readonly layoutVariant = computed<FeaturesLayoutVariant>(() => {
    return this._settings().layoutVariant || 'icon-list';
  });

  /** Computed section image URL from settings */
  readonly sectionImageUrl = computed<string | undefined>(() => {
    const settings = this._settings();
    // Prefer sectionImageUrl, fallback to sectionImageId if it's a URL
    if (settings.sectionImageUrl) {
      return settings.sectionImageUrl;
    }
    if (settings.sectionImageId && typeof settings.sectionImageId === 'string' && settings.sectionImageId.startsWith('http')) {
      return settings.sectionImageId;
    }
    return undefined;
  });

  /**
   * Resolved features combining settings (id, icon, imageUrl) with content texts (title, description).
   * Supports both new pattern (settings.features + content.featureTexts) and legacy pattern (content.features).
   */
  readonly resolvedFeatures = computed<ResolvedFeature[]>(() => {
    const settings = this._settings();
    const content = this._content();

    // New pattern: features in settings, texts in content.featureTexts
    const settingsFeatures = settings.features || [];
    const featureTexts = content.featureTexts || {};

    if (settingsFeatures.length > 0) {
      return settingsFeatures.map(f => {
        // Resolve image URL: prefer imageUrl, fallback to imageId if it's a URL
        let imageUrl = f.imageUrl;
        if (!imageUrl && f.imageId && typeof f.imageId === 'string' && f.imageId.startsWith('http')) {
          imageUrl = f.imageId;
        }

        return {
          id: f.id,
          title: featureTexts[f.id]?.title || '',
          description: featureTexts[f.id]?.description || '',
          icon: f.icon || '',
          imageUrl,
        };
      });
    }

    // Legacy pattern: features array directly in content
    const legacyFeatures = content.features || [];
    return legacyFeatures.map((f, i) => ({
      id: `legacy-${i}`,
      title: f.title || '',
      description: f.description || '',
      icon: f.icon || '',
      imageUrl: f.imageUrl,
    }));
  });

  /** Get SVG path for icon name, with fallback to checkmark */
  getIconPath(iconName: string): string {
    return ICON_PATHS[iconName] || ICON_PATHS['check'];
  }
}
